import React, { useState } from 'react';
import { Typography, Table, Space, Button, Modal, Form, Input, Select, message } from 'antd';
import { UserOutlined, AppstoreOutlined, PlusOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { functions } from '../../../shared/lib/supabase';
import { useNavigate } from 'react-router-dom';

const { Title, Paragraph } = Typography;

interface UserProfile {
  id: string;
  email: string;
  full_name: string | null;
  role: string;
  created_at: string;
  last_login?: string | null;
}

export const UsersPage: React.FC = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [form] = Form.useForm();

  const { data: clients = [], isLoading: loadingClients } = useQuery({
    queryKey: ['admin-users-list'],
    queryFn: async () => {
      try {
        const response = await functions.listClients();
        if (!response.success) {
          throw new Error(response.error || 'Failed to fetch clients');
        }
        return response.clients as UserProfile[];
      } catch (error) {
        console.error('Error fetching clients:', error);
        throw error;
      }
    },
  });

  const createUserMutation = useMutation({
    mutationFn: async (userData: { email: string; password: string; full_name: string; role: 'client' | 'admin' }) => {
      const response = await functions.createUser(userData);
      if (!response.success) {
        throw new Error(response.error || 'Failed to create user');
      }
      return response;
    },
    onSuccess: () => {
      message.success('Usuário criado com sucesso');
      queryClient.invalidateQueries({ queryKey: ['admin-users-list'] });
      setIsCreateModalOpen(false);
      form.resetFields();
    },
    onError: (error: Error) => {
      message.error(error.message || 'Erro ao criar usuário');
    },
  });

  const handleCreateUser = async (values: any) => {
    try {
      await createUserMutation.mutateAsync(values);
    } catch (error) {
      console.error('Error creating user:', error);
    }
  };

  const handleViewProducts = (user: UserProfile) => {
    navigate(`/admin/clients/${user.id}/products`);
  };

  const userColumns: ColumnsType<UserProfile> = [
    {
      title: 'Nome',
      dataIndex: 'full_name',
      key: 'full_name',
      render: (text) => (
        <Space>
          <UserOutlined />
          <span>{text || 'N/A'}</span>
        </Space>
      ),
    },
    {
      title: 'Email',
      dataIndex: 'email',
      key: 'email',
    },
      {
        title: 'Último acesso',
        dataIndex: 'last_login',
        key: 'last_login',
        render: (value: string | null | undefined) => value ? new Date(value).toLocaleString('pt-BR') : 'Nunca acessou',
      },
    {
      title: 'Cadastro',
      dataIndex: 'created_at',
      key: 'created_at',
      render: (date: string) => new Date(date).toLocaleDateString('pt-BR'),
    },
    {
      title: 'Ações',
      key: 'actions',
      render: (_, record) => (
        <Button type="primary" icon={<AppstoreOutlined />} onClick={() => handleViewProducts(record)}>
          Ver Produtos
        </Button>
      ),
    },
  ];

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16 }}>
        <Title level={2}>Usuários</Title>
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={() => setIsCreateModalOpen(true)}
        >
          Novo Usuário
        </Button>
      </div>
      <Paragraph>
        Gerencie os usuários e suas respectivas contratações de produtos.
      </Paragraph>
      <Table columns={userColumns} dataSource={clients} rowKey="id" loading={loadingClients} pagination={{ pageSize: 10 }} />

      <Modal
        title="Criar Novo Usuário"
        open={isCreateModalOpen}
        onCancel={() => {
          setIsCreateModalOpen(false);
          form.resetFields();
        }}
        onOk={() => form.submit()}
        confirmLoading={createUserMutation.isPending}
        closable={!createUserMutation.isPending}
        maskClosable={!createUserMutation.isPending}
        keyboard={!createUserMutation.isPending}
        cancelButtonProps={{ disabled: createUserMutation.isPending }}
        okText={createUserMutation.isPending ? 'Criando...' : 'Criar'}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleCreateUser}
          disabled={createUserMutation.isPending}
        >
          <Form.Item
            label="Email"
            name="email"
            rules={[
              { required: true, message: 'Por favor, insira o email' },
              { type: 'email', message: 'Email inválido' }
            ]}
          >
            <Input placeholder="usuario@exemplo.com" />
          </Form.Item>

          <Form.Item
            label="Senha"
            name="password"
            rules={[
              { required: true, message: 'Por favor, insira a senha' },
              { min: 6, message: 'A senha deve ter no mínimo 6 caracteres' }
            ]}
          >
            <Input.Password placeholder="Senha" />
          </Form.Item>

          <Form.Item
            label="Nome Completo"
            name="full_name"
            rules={[{ required: true, message: 'Por favor, insira o nome completo' }]}
          >
            <Input placeholder="Nome completo" />
          </Form.Item>

          <Form.Item
            label="Tipo de Usuário"
            name="role"
            initialValue="client"
            rules={[{ required: true, message: 'Por favor, selecione o tipo de usuário' }]}
          >
            <Select>
              <Select.Option value="client">Cliente</Select.Option>
              <Select.Option value="admin">Administrador</Select.Option>
            </Select>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};
